import React, { useState, useRef, useEffect } from 'react';
import { Download } from 'react-iconly';

const QRCodeGenerator = ({ storeId, storeName = 'Your Shop' }) => {
  const [qrSize] = useState(256);
  const canvasRef = useRef(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');

  // Generate the storefront URL
  const storeUrl = `${window.location.origin}/storefront/${storeId}`;

  // Generate QR code using canvas
  useEffect(() => {
    if (!canvasRef.current || !storeUrl) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, qrSize, qrSize);
    
    // Set background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, qrSize, qrSize);
    
    // Generate simple QR-like pattern (for demo purposes)
    // In production, you'd use a proper QR library
    ctx.fillStyle = 'black';
    const cellSize = qrSize / 25;
    
    // Create a simple pattern that looks like a QR code
    for (let i = 0; i < 25; i++) {
      for (let j = 0; j < 25; j++) {
        // Create a deterministic pattern based on the URL
        const shouldFill = (i * 25 + j + storeUrl.charCodeAt((i + j) % storeUrl.length)) % 3 === 0;
        if (shouldFill) {
          ctx.fillRect(i * cellSize, j * cellSize, cellSize, cellSize);
        }
      }
    }
    
    // Add corner markers (QR code standard)
    ctx.fillStyle = 'black';
    // Top-left corner
    ctx.fillRect(0, 0, 7 * cellSize, 7 * cellSize);
    ctx.fillStyle = 'white';
    ctx.fillRect(cellSize, cellSize, 5 * cellSize, 5 * cellSize);
    ctx.fillStyle = 'black';
    ctx.fillRect(2 * cellSize, 2 * cellSize, 3 * cellSize, 3 * cellSize);
    
    // Top-right corner
    ctx.fillStyle = 'black';
    ctx.fillRect(18 * cellSize, 0, 7 * cellSize, 7 * cellSize);
    ctx.fillStyle = 'white';
    ctx.fillRect(19 * cellSize, cellSize, 5 * cellSize, 5 * cellSize);
    ctx.fillStyle = 'black';
    ctx.fillRect(20 * cellSize, 2 * cellSize, 3 * cellSize, 3 * cellSize);
    
    // Bottom-left corner
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 18 * cellSize, 7 * cellSize, 7 * cellSize);
    ctx.fillStyle = 'white';
    ctx.fillRect(cellSize, 19 * cellSize, 5 * cellSize, 5 * cellSize);
    ctx.fillStyle = 'black';
    ctx.fillRect(2 * cellSize, 20 * cellSize, 3 * cellSize, 3 * cellSize);
    
    // Convert to data URL
    setQrCodeDataUrl(canvas.toDataURL('image/png'));
  }, [storeUrl, qrSize]);

  // Download QR code as PNG
  const downloadPNG = () => {
    if (!canvasRef.current) return;
    
    const link = document.createElement('a');
    link.download = `store-${storeId}-qrcode.png`;
    link.href = qrCodeDataUrl;
    link.click();
  };

  // Download QR code as SVG (simplified version)
  const downloadSVG = () => {
    const svgContent = `
      <svg width="${qrSize}" height="${qrSize}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="white"/>
        <text x="50%" y="50%" text-anchor="middle" dy=".3em" font-family="Arial" font-size="14" fill="black">
          QR Code for ${storeUrl}
        </text>
      </svg>
    `;
    
    const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    
    const link = document.createElement('a');
    link.href = svgUrl;
    link.download = `store-${storeId}-qrcode.svg`;
    link.click();
    
    URL.revokeObjectURL(svgUrl);
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      padding: 24,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      maxWidth: 400
    }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{
          fontSize: 18,
          fontWeight: 600,
          color: '#222',
          margin: '0 0 8px 0',
          fontFamily: 'Sora, sans-serif'
        }}>
          QR Code for Your Shop
        </h3>
        <p style={{
          fontSize: 14,
          color: '#757575',
          margin: 0,
          lineHeight: 1.4,
          fontFamily: 'Montserrat, sans-serif'
        }}>
          Share this QR code to let customers quickly find your shop.
        </p>
      </div>

      {/* QR Code Display */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 20,
        padding: '20px 0'
      }}>
        <div style={{
          background: 'white',
          padding: 16,
          borderRadius: 8,
          border: '1px solid #eee'
        }}>
          <canvas
            ref={canvasRef}
            width={qrSize}
            height={qrSize}
            style={{
              display: 'block',
              maxWidth: '100%',
              height: 'auto'
            }}
          />
        </div>
      </div>

      {/* Store URL Display */}
      <div style={{
        background: '#f8f9fa',
        borderRadius: 8,
        padding: 12,
        marginBottom: 20,
        border: '1px solid #e9ecef'
      }}>
        <p style={{
          fontSize: 12,
          color: '#6c757d',
          margin: '0 0 4px 0',
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 500
        }}>
          Store URL:
        </p>
        <p style={{
          fontSize: 13,
          color: '#495057',
          margin: 0,
          fontFamily: 'Monaco, Menlo, monospace',
          wordBreak: 'break-all'
        }}>
          {storeUrl}
        </p>
      </div>

      {/* Download Buttons */}
      <div style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap'
      }}>
        <button
          onClick={downloadPNG}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: '#C97B53',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'Montserrat, sans-serif',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.background = '#B86A42'}
          onMouseLeave={(e) => e.target.style.background = '#C97B53'}
        >
          <Download set="bold" size={16} />
          Download PNG
        </button>
        
        <button
          onClick={downloadSVG}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'transparent',
            color: '#C97B53',
            border: '1px solid #C97B53',
            borderRadius: 8,
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'Montserrat, sans-serif',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#C97B53';
            e.target.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.color = '#C97B53';
          }}
        >
          <Download set="bold" size={16} />
          Download SVG
        </button>
      </div>

      {/* Instructions */}
      <div style={{
        marginTop: 20,
        padding: 16,
        background: '#f8f9fa',
        borderRadius: 8,
        border: '1px solid #e9ecef'
      }}>
        <h4 style={{
          fontSize: 14,
          fontWeight: 600,
          color: '#495057',
          margin: '0 0 8px 0',
          fontFamily: 'Sora, sans-serif'
        }}>
          How to use:
        </h4>
        <ul style={{
          fontSize: 12,
          color: '#6c757d',
          margin: 0,
          paddingLeft: 16,
          lineHeight: 1.4,
          fontFamily: 'Montserrat, sans-serif'
        }}>
          <li>Download the QR code in your preferred format</li>
          <li>Add it to flyers, posters, or packaging</li>
          <li>Customers can scan to visit your shop directly</li>
        </ul>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
